// ─── Device Register Map ──────────────────────────────────────────────────────
//
// Learns which registers are valid/invalid for a specific device model by
// recording Modbus exception 0x02 (Illegal Data Address) responses.
// Used by the adaptive batch reader to skip known-invalid registers.
//
// The cache is keyed by full serial number so each device has its own
// register validity knowledge. Different firmware versions of the same
// model (e.g. ZM1 V250 vs V340) may support different registers.

export class DeviceRegisterMap {
  private static instances = new Map<string, DeviceRegisterMap>();

  private readonly invalidRegisters = new Set<number>();
  private _lastUpdated = 0;

  private constructor(readonly modelId: string) {}

  /** Get or create a register map for a device model. */
  static forModel(modelId: string): DeviceRegisterMap {
    if (!DeviceRegisterMap.instances.has(modelId)) {
      DeviceRegisterMap.instances.set(modelId, new DeviceRegisterMap(modelId));
    }
    return DeviceRegisterMap.instances.get(modelId)!;
  }

  /** Get or create a register map from a serial number. */
  static forSerial(serial: string): DeviceRegisterMap {
    const key = serial ? serial.toUpperCase() : 'UNKNOWN';
    return DeviceRegisterMap.forModel(key);
  }

  /** Clear all cached register maps (e.g. on logout). */
  static clearAll(): void {
    DeviceRegisterMap.instances.clear();
  }

  get lastUpdated(): number {
    return this._lastUpdated;
  }

  /** Mark a register address as invalid (received Modbus exception 0x02). */
  markInvalid(address: number): void {
    if (!this.invalidRegisters.has(address)) {
      this.invalidRegisters.add(address);
      this._lastUpdated = Date.now();
    }
  }

  /** Check if a register address is known to be invalid. */
  isInvalid(address: number): boolean {
    return this.invalidRegisters.has(address);
  }

  /** Reset the invalid register cache (e.g. after firmware update). */
  reset(): void {
    this.invalidRegisters.clear();
    this._lastUpdated = Date.now();
  }

  /** Number of known-invalid registers. */
  get invalidCount(): number {
    return this.invalidRegisters.size;
  }
}
